import { useState } from "react";
import { Check, Copy, Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PlaceholderNote } from "../components/ComingSoon";
import { LiveForm, Field, TextInput, TextArea } from "../components/form";
import { CONTACT, DONATIONS, IMAGES, SITE, SPONSOR, VOLUNTEER } from "../content/site";

const Section = ({ children, className = "", id, labelledBy }) => (
  <section id={id} aria-labelledby={labelledBy} className={`scroll-mt-28 px-4 py-14 sm:px-8 sm:py-20 lg:px-16 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">{children}</p>
);

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
    {children}
  </h2>
);

const parseAmounts = (text) => {
  const list = String(text || "")
    .split(",")
    .map((s) => parseFloat(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  return list.length ? list : [10, 25, 50, 100];
};

const DonateSection = () => {
  const amounts = parseAmounts(DONATIONS.amountsText);
  const [amount, setAmount] = useState(amounts[1] ?? amounts[0]);
  const [custom, setCustom] = useState("");
  return (
    <Section labelledBy="donate-heading" id="donate" className="bg-sage text-cream">
      <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2">
        <Reveal>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft sm:text-sm">Donate</p>
          <h2 id="donate-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
            {DONATIONS.heading}
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-cream/85">{DONATIONS.intro}</p>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {amounts.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => { setAmount(a); setCustom(""); }}
                aria-pressed={amount === a && !custom}
                className="min-h-[56px] rounded-xl border border-cream/30 text-lg font-semibold text-cream transition-colors hover:bg-cream/10 aria-pressed:border-gold aria-pressed:bg-gold aria-pressed:text-ink"
                data-testid={`donate-amount-${a}-button`}
              >
                ${a}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label htmlFor="donate-custom" className="text-sm font-semibold text-cream">
              Custom amount (USD)
            </label>
            <input
              id="donate-custom"
              type="number"
              min="1"
              inputMode="decimal"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Enter an amount"
              className="mt-1.5 block w-full min-h-[52px] rounded-xl border border-cream/30 bg-transparent px-4 py-3 text-lg text-cream placeholder:text-cream/50 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
              data-testid="donate-custom-amount-input"
            />
          </div>
          {DONATIONS.taxNote && DONATIONS.taxApproved && (
            <p className="mt-5 text-sm leading-relaxed text-cream/80" data-testid="donate-tax-note">
              {DONATIONS.taxNote}
            </p>
          )}
          <div className="mt-8 rounded-xl border border-gold/40 bg-sage-light/30 p-5" role="note" data-testid="donate-setup-note">
            <p className="text-sm leading-relaxed text-cream/90">
              <strong className="font-semibold text-gold-soft">Online giving isn't connected yet.</strong>{" "}
              {DONATIONS.setupNote}
            </p>
            {CONTACT.email ? (
              <a href={`mailto:${CONTACT.email}`} className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-ink hover:bg-gold-soft" data-testid="donate-email-link">
                <Mail className="h-4 w-4" aria-hidden="true" /> Email us about giving
              </a>
            ) : (
              <p className="mt-4 text-sm font-medium text-cream/70">
                In the meantime, the foundation's contact email will be published on the
                Contact page.
              </p>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <img
            src={IMAGES.unityHands}
            alt="Hands joined together in a circle, symbolizing community and mutual support"
            className="w-full rounded-2xl border border-cream/20 object-cover shadow-xl aspect-[4/5]"
            loading="lazy"
          />
        </Reveal>
      </div>
    </Section>
  );
};

const ShareSection = () => {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Section labelledBy="share-heading" id="share">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Eyebrow>Share the mission</Eyebrow>
          <H2 id="share-heading">Help someone find their way here</H2>
          <p className="mt-4 text-base text-body">
            Sharing this website may be the reason someone finds support. Copy the link
            below and pass it on.
          </p>
          <button
            type="button"
            onClick={copyLink}
            className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-colors hover:bg-sage-light"
            data-testid="share-copy-link-button"
          >
            {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            {copied ? "Link copied" : "Copy website link"}
          </button>
          <div className="mt-8 space-y-4">
            <PlaceholderNote testId="share-social-placeholder">
              <strong className="font-semibold text-ink">Social links — coming soon.</strong>{" "}
              Share buttons for the foundation's official social accounts will appear here
              once those accounts are confirmed.
            </PlaceholderNote>
            <PlaceholderNote testId="share-flyer-placeholder">
              <strong className="font-semibold text-ink">Downloadable flyer — coming soon.</strong>{" "}
              A printable Walking for Hope flyer will be available here for community
              boards, schools, and local businesses.
            </PlaceholderNote>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

export default function GetInvolved() {
  return (
    <>
      <PageHero eyebrow="Get involved" title="Help keep TJ's light shining" testId="involved-hero">
        <p>
          There are many ways to support this mission: show up, volunteer, give, share
          resources, or help bring the community together.
        </p>
      </PageHero>

      <Section labelledBy="volunteer-heading" id="volunteer">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <Eyebrow>Volunteer</Eyebrow>
            <H2 id="volunteer-heading">Lend your time and heart</H2>
            <p className="mt-4 text-base text-body">{VOLUNTEER.intro}</p>
          </Reveal>
          <Reveal className="mt-8">
            <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8">
              {VOLUNTEER.enabled !== false ? (
                <LiveForm
                  type="volunteer"
                  testId="volunteer-form"
                  submitLabel="Send volunteer interest"
                  successMessage="Thank you — your volunteer interest has been received."
                >
                  <div className="space-y-6">
                    <Field label="Name" id="vol-name" required>
                      <TextInput id="vol-name" required placeholder="Your full name" testId="vol-name-input" />
                    </Field>
                    <Field label="Contact information" id="vol-contact" required hint="Email or phone — whichever you prefer.">
                      <TextInput id="vol-contact" required placeholder="Email address or phone number" testId="vol-contact-input" />
                    </Field>
                    <fieldset>
                      <legend className="text-sm font-semibold text-ink">I'm interested in</legend>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {["Event day help", "Setup and planning", "Community outreach", "Behind-the-scenes support"].map((opt) => (
                          <label key={opt} className="flex min-h-[48px] cursor-pointer items-center gap-2.5 rounded-lg border border-sage/15 bg-paper/50 px-4 py-3 text-sm font-medium text-ink has-[:checked]:border-sage has-[:checked]:bg-sage-soft">
                            <input type="checkbox" name="vol-interest" value={opt} className="h-4 w-4 accent-[#0A2547]" data-testid={`vol-interest-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}-checkbox`} />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <Field label="Availability" id="vol-availability" hint="Rough is fine — e.g. weekends, evenings, event day only.">
                      <TextArea id="vol-availability" rows={3} placeholder="When are you generally available?" testId="vol-availability-textarea" />
                    </Field>
                  </div>
                </LiveForm>
              ) : (
                <PlaceholderNote testId="volunteer-closed-note">
                  <strong className="font-semibold text-ink">Volunteer sign-up is paused.</strong>{" "}
                  The foundation isn't collecting volunteer responses right now.
                </PlaceholderNote>
              )}
            </div>
          </Reveal>
        </div>
      </Section>

      <DonateSection />

      <Section labelledBy="sponsor-heading" id="sponsor">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <Eyebrow>Sponsor or partner</Eyebrow>
            <H2 id="sponsor-heading">Stand with this community</H2>
            <p className="mt-4 text-base text-body">{SPONSOR.intro}</p>
            {SPONSOR.packagesNote && (
              <div className="mt-6">
                <PlaceholderNote testId="sponsor-packages-placeholder">
                  <strong className="font-semibold text-ink">Sponsorship details.</strong>{" "}
                  {SPONSOR.packagesNote}
                </PlaceholderNote>
              </div>
            )}
          </Reveal>
          <Reveal className="mt-8">
            <div id="partner" className="scroll-mt-28 rounded-2xl border border-sage/10 bg-white p-6 sm:p-8">
              {SPONSOR.enabled !== false ? (
                <LiveForm
                  type="sponsor"
                  testId="sponsor-inquiry-form"
                  submitLabel="Send inquiry"
                  successMessage="Thank you — your inquiry has been received."
                >
                  <div className="space-y-6">
                    <Field label="Your name" id="sp-name" required>
                      <TextInput id="sp-name" required placeholder="Your full name" testId="sp-name-input" />
                    </Field>
                    <Field label="Organization" id="sp-org">
                      <TextInput id="sp-org" placeholder="Business, school, or group name" testId="sp-org-input" />
                    </Field>
                    <Field label="Contact information" id="sp-contact" required>
                      <TextInput id="sp-contact" required placeholder="Email address or phone number" testId="sp-contact-input" />
                    </Field>
                    <Field label="How would you like to help?" id="sp-message">
                      <TextArea id="sp-message" rows={4} placeholder="Sponsorship, partnership, in-kind support…" testId="sp-message-textarea" />
                    </Field>
                  </div>
                </LiveForm>
              ) : (
                <PlaceholderNote testId="sponsor-closed-note">
                  <strong className="font-semibold text-ink">Inquiries are paused.</strong>{" "}
                  The foundation isn't collecting sponsor or partner inquiries right now.
                </PlaceholderNote>
              )}
            </div>
          </Reveal>
        </div>
      </Section>

      <ShareSection />
    </>
  );
}
